import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, take, takeUntil } from 'rxjs';
import { AdminService } from 'src/app/services/admin.service';
import { LoginService } from 'src/app/services/login.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AgencyCDSDialogComponent } from 'src/app/shared/components/agency-cds-dialog/agency-cds-dialog.component';

type CDSRequestViewModelWithDisplay = ServerModels.CDSRequestViewModel & { StatusDisplay, Action, StateDateDisplay }

@Component({
  selector: 'ness-manage-my-agency',
    templateUrl: './manage-my-agency.component.html',
    providers: [DialogService, DatePipe]
})
export class ManageMyAgencyComponent implements OnInit {

  readonly myAgencyFilterStorageKey: string = 'myAgencyFilterStorageKey';
  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _userInfo: ServerModels.UserInfoViewModel = null;
  private manualDialogRef: DynamicDialogRef = null;

  rows: number = AppConstants.defaultTableRows;
  allCdsData: CDSRequestViewModelWithDisplay[] = [];
  cdsData: CDSRequestViewModelWithDisplay[] = [];
  selectedData: CDSRequestViewModelWithDisplay = null;
  selectedFilter: string;

  readonly cdsReceivedValue: string = 'CDSRequestReceived';
  readonly cdsRequestValue: string = 'CDSRequestsSent';
  readonly cdsAvailableAgencyValue: string = 'AvailableAgencies';
  readonly cdsSharingValue: string = 'CDSSharing';
  readonly cdsDenialValue: string = 'CDSDenials';
  readonly cdsAllAgencyValue: string = 'CDSAllAgencies';
  readonly submitRequestHeader: string = 'Data Sharing Agreement with [Requesting Agency]';
  readonly submitRequestMessage: string = 'You are agreeing to share your Agency’s NESS data with Receiving Agency. If Receiving Agency agrees to share their NESS data with your Agency, both Agencies will see each other’s NESS data immediately. NESS data will include your NIBIN Case Data, Linked Crime Guns, RMS Event Data (people, locations, etc.). You can stop sharing your Agency’s NESS data with Receiving Agency any time by returning to this page. You can also stop sharing your Agency’s NESS data with ALL Agencies at any time by sending an email <a href="mailto:ness_support@atf.gov" class="blue-hyperlink">NESS_Support@atf.gov</a>.';

  readonly requestReceivedHeader: string = 'Data Sharing Agreement with [Requesting Agency]';
  readonly requestReceivedMessage: string = 'You are agreeing to share your Agency’s NESS data with Requesting Agency. NESS data will include your NIBIN Case Data, Linked Crime Guns, RMS Event Data (people, locations, etc.). You can stop sharing your Agency’s NESS data with Requesting Agency any time by returning to this page. You can also stop sharing your Agency’s NESS data with ALL Agencies at any time by sending an email <a href="mailto:ness_support@atf.gov" class="blue-hyperlink">NESS_Support@atf.gov</a>.';

  CDSRequestReceivedCount: number = 0;
  CDSRequestsSentCount: number = 0;
  CDSAvailableAgenciesCount: number = 0;
  CDSSharingCount: number = 0;
  CDSDenialsCount: number = 0;
  CDSAllAgenciesCount: number = 0;

  confirmHeader: string = '';
  confirmMessage: string = '';
  diableActionButton: boolean = false;


  constructor(
    public dialogService: DialogService,
    private loginService: LoginService,
    private adminService: AdminService
  ) {
    this.loginService.userInfoData$.pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (info: ServerModels.UserInfoViewModel) => this._userInfo = info
    });
  }

  cols: any[] = [
    { header: 'Status', field: 'StatusDisplay' },
    { header: 'Status Date', field: 'StateDateDisplay' },
    { header: 'Agency', field: 'AgencyName' },
    { header: 'Agency POC', field: 'AgencyPOC' },
    { header: 'Agency POC Phone', field: 'AgencyPOCPhone' },
    { header: 'Agency POC Email', field: 'AgencyPOCEmail' }
  ];

  ngOnInit(): void {
    let filterValue = sessionStorage.getItem(this.myAgencyFilterStorageKey);
    this.selectedFilter = filterValue || this.cdsAvailableAgencyValue;
    let t = this._userInfo;
    this.getCDSData(this.selectedFilter);   
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private setFilterValuesLength(data: CDSRequestViewModelWithDisplay[]) {
    this.CDSRequestReceivedCount = data.filter(x => x.StatusName == 'Pending' && !x.FromMyAgency).length;
    this.CDSRequestsSentCount = data.filter(x => x.StatusName == 'Pending' && x.FromMyAgency).length;
    this.CDSAvailableAgenciesCount = data.filter(x => x.StatusName == 'Available').length;
    this.CDSSharingCount = data.filter(x => x.StatusName == 'Sharing').length;
    this.CDSDenialsCount = data.filter(x => x.StatusName == 'Denied').length;
    this.CDSAllAgenciesCount = data.length;
  }

  public handleChange(value: string): void {
    this.UpdateSessionFilterValue(value);
    if (!!this.allCdsData) {
      this.setCDSTableValue(value);
    }
    else {
      this.getCDSData(value);
    }
  }

  private getCDSData(filterValue: string) {
    this.adminService.getAllCDSRequests().pipe(takeUntil(this._unsubscribe$)).subscribe(
      (data: ServerModels.CDSRequestViewModel[]) => {
        if (data) {
          this.allCdsData = [];
          data.forEach(x => {
            this.allCdsData.push(this.extendViewModel(x));
          });          
          this.setCDSTableValue(filterValue);          
        }
      });
  }

  private extendViewModel(data: ServerModels.CDSRequestViewModel): CDSRequestViewModelWithDisplay{
    const dataWithDisplay = data as CDSRequestViewModelWithDisplay;
    dataWithDisplay.StateDateDisplay = new DatePipe('en-US').transform(dataWithDisplay.StatusDate, 'MM/dd/yyyy');
    switch (data.StatusName) {
      case 'Pending': {
        dataWithDisplay.StatusDisplay = !data.FromMyAgency ? 'Pending Your Approval' : 'Pending ' + data.AgencyName + ' Approval';
        dataWithDisplay.Action = !data.FromMyAgency ? 'Approve' : 'Cancel';
        break;
      }
      case 'Available': {
        dataWithDisplay.StatusDisplay = 'Available to Request';
        dataWithDisplay.Action = "Request";
        break;
      }
      case 'Sharing': {
        dataWithDisplay.StatusDisplay = 'Sharing Data';
        dataWithDisplay.Action = "Stop Sharing";
        break;
      }
      case 'Denied': {
        dataWithDisplay.StatusDisplay = data.FromMyAgency ? 'Denied by ' + data.AgencyName : 'Request Denied';
        dataWithDisplay.Action = data.FromMyAgency ? null : 'Approve';
        break;
      }
    }
    return dataWithDisplay;
  }

  private setCDSTableValue(filterValue: string) {
    if (!!this.allCdsData) {
      this.cdsData = [];
      switch (filterValue) {
        case this.cdsReceivedValue: {
          this.cdsData = [...this.allCdsData.filter(x => x.StatusName == 'Pending' && !x.FromMyAgency)];
          break;
        }
        case this.cdsRequestValue: {
          this.cdsData = [...this.allCdsData.filter(x => x.StatusName == 'Pending' && x.FromMyAgency)];
          break;
        }
        case this.cdsAvailableAgencyValue: {
          this.cdsData = [...this.allCdsData.filter(x => x.StatusName == 'Available')];
          break;
        }
        case this.cdsSharingValue: {
          this.cdsData = [...this.allCdsData.filter(x => x.StatusName == 'Sharing')];
          break;
        }
        case this.cdsDenialValue: {
          this.cdsData = [...this.allCdsData.filter(x => x.StatusName == 'Denied')];
          break;
        }
        case this.cdsAllAgencyValue: {      
          this.cdsData = [...this.allCdsData];
          break;
        }
      }
      this.setFilterValuesLength(this.allCdsData);
    }
  }

  public onRequestUpdate(selectedData: CDSRequestViewModelWithDisplay, index: number, action: string): void {
    switch (action) {
      case 'Approve': {
        const searchRegExp = /Requesting Agency/gi;
        this.confirmHeader = this.requestReceivedHeader.replace('[Requesting Agency]', selectedData.AgencyName);
        this.confirmMessage = this.requestReceivedMessage.replace(searchRegExp, selectedData.AgencyName);
        this.displayDialog(selectedData, action);
        break;
      }
      case 'Deny': {
        this.denyRequest(selectedData);
        break;
      }
      case 'Request': {
        const searchRegExp = /Receiving Agency/gi;
        this.confirmHeader = this.submitRequestHeader.replace('[Requesting Agency]', selectedData.AgencyName);
        this.confirmMessage = this.submitRequestMessage.replace(searchRegExp, selectedData.AgencyName);
        this.displayDialog(selectedData, action);
        break;
      }
      case 'Cancel': {
        this.cancelRequest(selectedData);
        break;
      }
      case 'Stop Sharing': {
        this.stopSharingRequest(selectedData);
        break;
      }
    }    
  }

  private displayDialog(selectedData: CDSRequestViewModelWithDisplay, action: string) {
    this.diableActionButton = true;
    this.manualDialogRef = this.dialogService.open(AgencyCDSDialogComponent, {
      header: this.confirmHeader,
      dismissableMask: true,
      modal: true,
      contentStyle: {
        'overflow-y': 'auto',
        'overflow-x': 'hidden',
        'max-height': '80vh',
        'min-height': '20vh',
      },
      styleClass: 'cds-dialog w-sm-75 w-lg-50',
      baseZIndex: 1000000,
      data: {
        data: selectedData,
        action: action,
        message: this.confirmMessage,
        dtnDisplayName: 'I Agree'
      },
    });

    this.manualDialogRef.onClose.pipe(take(1)).subscribe((data: any) => {
      if (data) {
        switch (data.Action) {
          case 'Approve': {
            this.approveRequest(data);
            break;
          }
          case 'Request': {
            this.createRequest(data);
            break;
          }
          default: {
            this.diableActionButton = false;
          }
        }
      }
      else {
        this.diableActionButton = false;
      }
    });
  }

  private UpdateSessionFilterValue(value: string): void {
    sessionStorage.setItem(this.myAgencyFilterStorageKey, value);
  }

  private createRequest(cdsRequest: CDSRequestViewModelWithDisplay) {
    this.adminService.createCDSRequest(cdsRequest.AgencyID).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (data) => {
        let dataWithDisplay = null;
        this.diableActionButton = false;
        if (data) {
          dataWithDisplay = data as CDSRequestViewModelWithDisplay;
          dataWithDisplay.StatusDisplay = 'Pending ' + data.AgencyName + ' Approval';
          dataWithDisplay.Action = 'Cancel';
          dataWithDisplay.StateDateDisplay = new DatePipe('en-US').transform(dataWithDisplay.StatusDate, 'MM/dd/yyyy');
          this.refreshCDSData(dataWithDisplay); 
        }
      },
      error: (err) => {
        this.diableActionButton = false;
      }
    });
  }

  private approveRequest(cdsRequest: CDSRequestViewModelWithDisplay) {
    this.adminService.approveCDSRequest(cdsRequest.RequestID).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (data) => {
        this.diableActionButton = false;
        if (data) {
          cdsRequest.StatusDisplay = 'Sharing Data';
          cdsRequest.StatusName = 'Sharing';
          cdsRequest.Action = "Stop Sharing";
          this.refreshCDSData(cdsRequest); 
        }        
      },
      error: (err) => {
        this.diableActionButton = false;
      }
    });
  }

  private cancelRequest(selectedData: CDSRequestViewModelWithDisplay) {
    this.adminService.cancelCDSRequest(selectedData.RequestID).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (data) => {
        if (data) {
          selectedData.RequestID = null;
          selectedData.StatusDisplay = 'Available to Request';
          selectedData.StatusName = 'Available';
          selectedData.Action = "Request";
          this.refreshCDSData(selectedData);
        }
      });
  }

  private denyRequest(selectedData: CDSRequestViewModelWithDisplay) {
    this.adminService.denyCDSRequest(selectedData.RequestID).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (data) => {
        if (data) {
          selectedData.StatusDisplay = 'Request Denied';
          selectedData.StatusName = 'Denied';
          selectedData.Action = "Approve";          
          this.refreshCDSData(selectedData);
        }
      });
  }

  private stopSharingRequest(selectedData: CDSRequestViewModelWithDisplay) {
    this.adminService.stopCDSRequest(selectedData.RequestID).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (data) => {
        if (data) {
          selectedData.StatusDisplay = 'Available to Request';
          selectedData.StatusName = 'Available';
          selectedData.Action = "Request";
          this.refreshCDSData(selectedData);
        }       
      });
  }

  private refreshCDSData(data: CDSRequestViewModelWithDisplay) {
    if (data && !!data.RequestID) {
        const idx = this.allCdsData.findIndex(x => x.RequestID == data.RequestID);
        if (idx > -1) {
          this.allCdsData[idx] = data;
        } else {
          this.getCDSData(this.selectedFilter);
        }
        this.setCDSTableValue(this.selectedFilter);  
    }
    else {
      this.getCDSData(this.selectedFilter);
    }
  }
}
